import { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ConciergeSidebar, signOut, useConciergeContext } from '@brandclub/common-ui';
import { useAppSelector } from '@/redux/hooks';
import { preloadChatHistory } from '@/components/routes/appPageRoutes';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import ConciergeFullScreenLoading from '@/components/StoreComponents/StoreFullScreenLoading/ConciergeFullScreenLoading';
import './concierge-tailwind.css';

const getCookie = (name: string): string => {
  try {
    const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
    return match?.split('=')[1] ?? '';
  } catch {
    return '';
  }
};

const getVisitorId = (): string => {
  return getCookie('_vs_id');
};

const ConciergeOutlet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const conversationState = useConciergeContext();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const brandAppConfig = useAppSelector((state) => state.appConfig?.brandAppConfig);

  const splitAttributes = {
    email: userProfile?.email ?? '',
    domain: window.location.hostname,
    appConfigId: brandAppConfig?.appConfigId ?? '',
    appId: brandAppConfig?.appId ?? '',
    visitorId: getVisitorId(),
  };
  const { treatments } = useSplitTreatments({
    names: ['brandclub_dtc_concierge'],
    attributes: splitAttributes,
  });

  const CONCIERGE_ALLOWED_HOSTS: string[] = [];

  const isConciergeEnabled =
    treatments?.brandclub_dtc_concierge?.treatment === 'on' ||
    window.location.hostname.includes('localhost') ||
    CONCIERGE_ALLOWED_HOSTS.some((h) => window.location.hostname === h) ||
    (localStorage.getItem('conciergeEnabledVersion') ?? 'v0.0.0') >= 'v1.0.0';

  const viewMode = useMemo<'read-only' | undefined>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('viewToken') ? 'read-only' : undefined;
  }, []);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const sidebarUserProfile = useMemo(
    () => ({
      firstName: userProfile?.given_name,
      lastName: userProfile?.family_name,
      isSignedIn: !!userProfile?.userId,
    }),
    [userProfile],
  );

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      conversationState.setConversationHistory([]);
      conversationState.setFollowUpQuestions([]);
      conversationState.clearStreamingState();
      conversationState.setIsSharedView(false);
      conversationState.setSharedByUserName(undefined);
      conversationState.setSharedAtMessageIndex(undefined);
      conversationState.setSourceConversationId(undefined);
      conversationState.setSourceCheckpointIndex(undefined);
      conversationState.setSharedConversationId(null);
      conversationState.setConversationId(conversationId);
      navigate('/concierge');
    },
    [conversationState, navigate],
  );

  const handleComposeClick = useCallback(() => {
    conversationState.setConversationHistory([]);
    conversationState.setFollowUpQuestions([]);
    conversationState.setStreamingAIResponse('');
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(undefined);
    conversationState.setSharedAtMessageIndex(undefined);
    conversationState.setSourceConversationId(undefined);
    conversationState.setSourceCheckpointIndex(undefined);
    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    navigate('/concierge');
  }, [conversationState, navigate]);

  const handleChatsClick = useCallback(() => {
    const isOnHistory = location.pathname.includes('/concierge/history');
    if (isOnHistory) {
      if (!isSidebarExpanded) setIsSidebarExpanded(true);
      return;
    }
    navigate('/concierge/history');
  }, [navigate, location.pathname, isSidebarExpanded]);

  const handleHomeClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleProfileClick = useCallback(() => {
    window.open('/me', '_blank');
  }, []);

  const handlePurchasesClick = useCallback(() => {
    window.open('/purchase', '_blank');
  }, []);

  const handleSignOut = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('conversationId');
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    await signOut();
  }, []);

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarExpanded((prev) => !prev);
  }, []);

  const handleShareChat = useCallback(
    (conversationId: string) => {
      conversationState.setPendingShareConversationId(conversationId);
    },
    [conversationState],
  );

  const handleSignInClick = useCallback(() => {
    navigate('/signin');
  }, [navigate]);

  useEffect(() => {
    preloadChatHistory();
  }, []);

  useEffect(() => {
    if (!isConciergeEnabled) return;
    localStorage.setItem('conciergeEnabledVersion', 'v1.0.0');
  }, [isConciergeEnabled]);

  if (!isConciergeEnabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<ConciergeFullScreenLoading />}>
      <div
        className="concierge-root"
        style={{
          display: 'flex',
          flexDirection: 'row',
          height: '100dvh',
          minHeight: '-webkit-fill-available',
        }}
      >
        {viewMode !== 'read-only' && (
          <ConciergeSidebar
            onConversationSelect={handleConversationSelect}
            onComposeClick={handleComposeClick}
            onChatsClick={handleChatsClick}
            currentConversationId={conversationState.conversationId}
            userProfile={sidebarUserProfile}
            apiBaseUrl={conversationState.apiBaseUrl}
            onSignOutClick={handleSignOut}
            onProfileClick={handleProfileClick}
            onPurchasesClick={handlePurchasesClick}
            onHomeClick={handleHomeClick}
            isExpanded={isSidebarExpanded}
            onToggle={handleSidebarToggle}
            onShareChat={handleShareChat}
            onSignInClick={handleSignInClick}
          />
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </div>
      </div>
    </Suspense>
  );
};

export default ConciergeOutlet;
